import { useState, useRef } from 'react'
import { LockClosedIcon, UserIcon } from '@heroicons/react/24/solid'
import PropTypes from 'prop-types'
import ButtonEl from '../helpers/ctas/Button'
import { dataLayerPush } from '../../helpers/thirdparty'
import { getCsrfToken } from '../../helpers/memberSession'

const defaultSignInTitle = 'Sign in to continue and access member only content'
const defaultRegistrationPromo = "Can't sign in?\nBecome an NDS member today."

/**
 * The gate banner that appears when a visitor hits a members-only page.
 *
 * Left panel  (dark blue) — in-place sign-in form. Credentials are POSTed to
 *   /umbraco/surface/memberlogin/login-ajax and the page is reloaded on success
 *   so Umbraco's session cookie takes effect and the banner disappears.
 * Right panel (gold)      — a prompt to join for people without an account.
 *
 * All copy and URLs come from the Website node in the CMS
 * (Membership tab → lockedContentBannerSettings).
 */
export default function LockedContentBanner({
  forgotPasswordUrl,
  joinUrl,
  signInTitle,
  registrationPromoText,
  loginButtonText,
  forgotPasswordText,
  joinButtonLabel,
}) {
  const emailRef = useRef(null)
  const passwordRef = useRef(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  const signInCopy = (signInTitle && signInTitle.trim()) || defaultSignInTitle
  const joinCopy = (registrationPromoText && registrationPromoText.trim()) || defaultRegistrationPromo

  const handleLogin = async (e) => {
    e.preventDefault()
    const email = emailRef.current?.value?.trim() ?? ''
    const password = passwordRef.current?.value ?? ''

    if (!email || !password) {
      setError('Please enter your email and password.')
      return
    }

    setError(null)
    setLoading(true)

    try {
      dataLayerPush({
        event: 'login_intent',
        click_text: loginButtonText?.trim() || 'Login',
        page_section: 'Lock Content Banner',
        category: null,
        member_only: true,
      })

      const res = await fetch('/umbraco/surface/memberlogin/login-ajax', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'RequestVerificationToken': getCsrfToken(),
        },
        body: JSON.stringify({ email, password }),
      })

      const data = await res.json()

      if (data.success) {
        // Reload so the now-unlocked content renders in place — don't bounce the member off the
        // page they were trying to view.
        window.location.reload()
        return
      }

      setError(data.error ?? 'Login failed. Please try again.')
    } catch {
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="grid md:grid-cols-2 lg:container lg:mx-auto">
      {/* ── Left panel — sign in ── */}
      <div className="flex flex-col items-center text-center text-white px-8 py-12 gap-6 bg-nds-dark-blue" data-bg="0b304f">
        <LockClosedIcon className="h-12 w-12 shrink-0" aria-hidden="true" />

        <p className="text-2xl font-light max-w-xs leading-snug whitespace-pre-line">{signInCopy}</p>

        <form onSubmit={handleLogin} className="w-full max-w-xs flex flex-col gap-4 text-left" noValidate>
          {error && (
            <p role="alert" className="text-sm bg-red-800 text-white rounded px-3 py-2">
              {error}
            </p>
          )}

          <div>
            <label htmlFor="lc-email" className="block font-base font-medium mb-1">
              Email address
            </label>
            <input
              id="lc-email"
              ref={emailRef}
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              className="w-full px-3 py-2 rounded text-sm bg-white! text-gray-900!"
            />
          </div>

          <div>
            <label htmlFor="lc-password" className="block font-base font-medium mb-1">
              Password
            </label>
            <input
              id="lc-password"
              ref={passwordRef}
              type="password"
              autoComplete="current-password"
              className="w-full px-3 py-2 rounded text-sm bg-white! text-gray-900!"
            />
          </div>

          <div className="flex items-center justify-between gap-4">
            <a
              href={forgotPasswordUrl}
              className="text-sm text-white font-medium shrink-0 hover:underline decoration-current hover:decoration-current underline-offset-4"
            >
              {forgotPasswordText?.trim() || 'Forgot your password?'}
            </a>

            <ButtonEl
              className="text-white text-sm font-semibold px-5 py-2 rounded shrink-0 hover:opacity-90 transition-opacity"
              theme="primary"
              type="submit"
              disabled={loading}
              skipCtaClickTracking
            >
              {loading ? 'Signing in…' : (loginButtonText?.trim() || 'Login')}
            </ButtonEl>
          </div>
        </form>
      </div>

      {/* ── Right panel — join prompt ── */}
      <div className="flex flex-col items-center justify-center text-center text-white px-8 py-12 gap-6 bg-nds-orange">
        <UserIcon className="h-12 w-12 shrink-0 text-(--sb-secondary-bg)" aria-hidden="true" />

        <p className="text-2xl font-light text-nds-dark-blue max-w-xs leading-snug whitespace-pre-line">{joinCopy}</p>

        <ButtonEl
          link={{ url: joinUrl }}
          theme="primary"
          dataBg="0b304f"
          className="!text-white !text-sm !font-semibold px-6! py-3! rounded-md! hover:opacity-90 bg-nds-dark-blue! border-nds-dark-blue! hover:bg-nds-dark-blue! hover:border-nds-dark-blue!"
          skipCtaClickTracking
          onClick={() => dataLayerPush({ event: 'signup_intent' })}
        >
          {joinButtonLabel?.trim() || 'Join now'}
        </ButtonEl>
      </div>
    </div>
  )
}

LockedContentBanner.propTypes = {
  /** Link to the forgot-password page. */
  forgotPasswordUrl: PropTypes.string,
  /** Link to the membership sign-up page. */
  joinUrl: PropTypes.string,
  /** Heading copy for the sign-in panel. */
  signInTitle: PropTypes.string,
  /** Body copy for the join panel. */
  registrationPromoText: PropTypes.string,
  /** Label for the login button. */
  loginButtonText: PropTypes.string,
  /** Label for the forgot-password link. */
  forgotPasswordText: PropTypes.string,
  /** Label for the join CTA button. */
  joinButtonLabel: PropTypes.string,
}

LockedContentBanner.defaultProps = {
  forgotPasswordUrl: '/member/forgot-password',
  joinUrl: '/membership',
  signInTitle: null,
  registrationPromoText: null,
  loginButtonText: null,
  forgotPasswordText: null,
  joinButtonLabel: null,
}
